import {
  isOpportunityIngestEnabled,
  buildFacetedSkillsFilterDef,
  buildTextContainsFilterDef,
  buildUnifiedMultiFilterDef,
  buildUnifiedSingleFilterDef,
} from '@yeshuman/sdk'
import type { FilterDefinition, TalentFacet } from '@yeshuman/sdk'
import { JOB_BROWSE_SKILL_MATCH_MODE, type JobBrowseFilters } from '../job-browse-filters'
import {
  CANDIDATE_JOBS_FILTER_ICONS,
  EMPLOYMENT_TYPE_LABELS,
  POSTED_SINCE_FILTER_OPTIONS,
  POSTED_SINCE_LABELS,
  SOURCE_FILTER_OPTIONS,
  SOURCE_KIND_LABELS,
  WORK_MODE_LABELS,
  type JobPostedSince,
  type JobSourceKind,
} from './candidate-jobs-constants'
import {
  UNIFIED_EMPLOYMENT_FILTER_OPTIONS,
  UNIFIED_WORK_MODE_FILTER_OPTIONS,
} from './job-meta-filter-options'

export type CandidateJobsFilterCtx = {
  skillFacets: TalentFacet[]
  skillFacetsLoading: boolean
  selectedSkillLabels: string[]
  showSourceFilter: boolean
}

export function getCandidateJobsFilterDefinitions(): FilterDefinition<
  JobBrowseFilters,
  CandidateJobsFilterCtx
>[] {
  const defs: FilterDefinition<JobBrowseFilters, CandidateJobsFilterCtx>[] = [
    buildFacetedSkillsFilterDef({
      icon: CANDIDATE_JOBS_FILTER_ICONS.skills,
      getSelectedSkills: (filters) => filters.selectedSkills,
      setSelectedSkills: (filters, selectedSkills) => ({ ...filters, selectedSkills, page: 1 }),
      getSkillFacets: (ctx) => ctx.skillFacets,
      isPickerCatalogLoading: (ctx) => ctx.skillFacetsLoading,
      reset: (filters) => ({
        ...filters,
        selectedSkills: [],
        skillMatchMode: JOB_BROWSE_SKILL_MATCH_MODE,
        page: 1,
      }),
      searchPlaceholder: 'Search skills...',
    }),
    buildUnifiedMultiFilterDef({
      key: 'work_mode',
      label: 'Work mode',
      icon: CANDIDATE_JOBS_FILTER_ICONS.work_mode,
      options: UNIFIED_WORK_MODE_FILTER_OPTIONS,
      labels: WORK_MODE_LABELS,
      getValues: (filters) => filters.work_mode,
      setValues: (filters, values) => ({ ...filters, work_mode: values, page: 1 }),
    }),
    buildUnifiedMultiFilterDef({
      key: 'employment_type',
      label: 'Employment',
      icon: CANDIDATE_JOBS_FILTER_ICONS.employment_type,
      options: UNIFIED_EMPLOYMENT_FILTER_OPTIONS,
      labels: EMPLOYMENT_TYPE_LABELS,
      getValues: (filters) => filters.employment_type,
      setValues: (filters, values) => ({ ...filters, employment_type: values, page: 1 }),
    }),
    buildUnifiedSingleFilterDef({
      key: 'posted',
      label: 'Posted',
      icon: CANDIDATE_JOBS_FILTER_ICONS.posted,
      options: POSTED_SINCE_FILTER_OPTIONS,
      labels: POSTED_SINCE_LABELS,
      getValue: (filters) => filters.published_since,
      setValue: (filters, value) => ({
        ...filters,
        published_since: value as JobPostedSince | '',
        page: 1,
      }),
    }),
    buildTextContainsFilterDef({
      key: 'location',
      label: 'Location',
      icon: CANDIDATE_JOBS_FILTER_ICONS.location,
      getValue: (filters) => filters.location,
      setValue: (filters, value) => ({ ...filters, location: value, page: 1 }),
      inputPlaceholder: 'City or region…',
    }),
    buildTextContainsFilterDef({
      key: 'organisation',
      label: 'Organisation',
      icon: CANDIDATE_JOBS_FILTER_ICONS.organisation,
      getValue: (filters) => filters.organisation,
      setValue: (filters, value) => ({ ...filters, organisation: value, page: 1 }),
      inputPlaceholder: 'Organisation name…',
    }),
  ]

  if (isOpportunityIngestEnabled()) {
    defs.splice(4, 0, buildUnifiedMultiFilterDef({
      key: 'source',
      label: 'Source',
      icon: CANDIDATE_JOBS_FILTER_ICONS.source,
      options: SOURCE_FILTER_OPTIONS,
      labels: SOURCE_KIND_LABELS,
      getValues: (filters) => filters.source,
      setValues: (filters, values) => ({
        ...filters,
        source: values as JobSourceKind[],
        page: 1,
      }),
    }))
  }

  return defs
}
